import {setInShallowClone} from '../../../util/utility';
import {ActionType, ViewReducerState} from '../reducerSupport';
import * as Actions from './actions';
import {arrayMove} from './arrayMove';
import * as Types from './types';

/**
 * This actions handles moving the active section to the target section's position in the array.
 * This reorders the sectionRefs in the panel bank config.
 *
 * @param ref This is the panel bank config ref
 * @param activeSectionRefId This is the section that is being moved
 * @param targetSectionRefId This is the section that indicates what position the active section will be moved to
 */
export const moveSectionAction = (
  state: ViewReducerState,
  ref: Types.Ref,
  activeSectionRefId: string,
  targetSectionRefId: string
): [ViewReducerState, ActionType] => {
  const sectionRefs = state.parts[ref.type][ref.id].sectionRefs;

  // Find the actual indices in redux. This is more robost than relying
  // on passed in indices from dnd-kit, etc.
  const actualActiveIndex = sectionRefs.findIndex(
    sectionRef => sectionRef.id === activeSectionRefId
  );
  const actualTargetIndex = sectionRefs.findIndex(
    sectionRef => sectionRef.id === targetSectionRefId
  );

  // Validate that both sections exist
  if (actualActiveIndex === -1 || actualTargetIndex === -1) {
    console.warn('moveSectionAction: Section not found', {
      activeSectionRefId,
      targetSectionRefId,
      activeIndex: actualActiveIndex,
      targetIndex: actualTargetIndex,
    });
    return [state, Actions.noOp(ref)];
  }

  // If moving to the same position, no-op
  if (actualActiveIndex === actualTargetIndex) {
    return [state, Actions.noOp(ref)];
  }

  // If the section we are moving to is pinned, we need to update the pinned state
  const targetSectionRef = sectionRefs[actualTargetIndex];
  const targetSection = state.parts[targetSectionRef.type][targetSectionRef.id];
  const isTargetSectionPinned = targetSection.pinned;

  const newSectionRefs = arrayMove(
    sectionRefs,
    actualActiveIndex,
    actualTargetIndex
  );

  let newState = setInShallowClone(
    state,
    ['parts', ref.type, ref.id, 'sectionRefs'],
    newSectionRefs
  );

  const activeSectionRef = sectionRefs[actualActiveIndex];
  newState = setInShallowClone(
    newState,
    ['parts', activeSectionRef.type, activeSectionRef.id, 'pinned'],
    isTargetSectionPinned
  );

  // For undo, we need to move the section back to its original position
  // The section that's now at the original position is what we should target
  const undoTargetSectionRef = newSectionRefs[actualActiveIndex];

  const undoAction = Actions.moveSection(
    ref,
    activeSectionRefId, // Move the section we just moved
    undoTargetSectionRef.id // To where the displaced section now is
  );

  return [newState, undoAction];
};
